import { ColSpec } from '@fullcalendar/resource-common';
import { useSnackbar } from 'notistack';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  TSiteProcessListData,
  TGanttData,
  TGanttVisibleRange,
  TGanttEvent,
  TGanttResource
} from '../../../../types/process';
import {
  convertToFullCalendarDateString,
  convertToFullCalendarEndDateString,
  createGanttEvent,
  createGanttResource,
  validateGanttPeriod
} from '../../../../utils';
import { checkResData } from '../../../../utils/common';
import {
  UseFormClearErrors,
  UseFormGetValues,
  UseFormReset
} from 'react-hook-form';
import {
  GANTT_CHART_HTML_ID,
  GANTT_MINIMUM_HEIGHT,
  GANTT_RESOURCE_MIN_WIDTH_RATIO,
  GANTT_CALENDAR_RESOURCE_WIDTH,
  GANTT_SEARCH_HTML_ID,
  validationMessage
} from '../../../../const/ProcessConst';
import { getCalendarList } from '../../../../apicalls/calendar';
import { Typography } from '@material-ui/core';
import {
  TCalendarListData,
  TCalendarParentSiteProcess
} from '../../../../types/calendar';
import { CustomTooltip } from '../../../tooltip/CustomTooltip';
import { tooltipContents } from '../../../tooltip/Const';

export type TProps = {
  userId: number;
  reset: UseFormReset<any>;
  getValues: UseFormGetValues<any>;
  clearErrors: UseFormClearErrors<any>;
};

export const useHooks = (props: TProps) => {
  const { userId, reset, getValues, clearErrors } = props;

  // 共通で利用する現在日時
  const today = new Date().toISOString().split('T')[0].replaceAll('-', '/');

  // fullcalendar参照用
  const calendarRef = useRef<any>();

  // snackbar設定
  const { enqueueSnackbar } = useSnackbar();

  // 工事情報
  const [selectedConstructId, setSelectedConstructId] = useState(0);

  // 工程情報
  const [selectedProcessId, setSelectedProcessId] = useState(0);

  // 初期表示時のガントチャートで表示するデータ
  const [initialGanttData, setInitialGanttData] = useState<TGanttData>({
    resources: [],
    events: []
  });

  // 初期表示時の表示期間のデータ
  const [initialVisibleRange, setInitialVisibleRange] =
    useState<TGanttVisibleRange>({
      start: '', // 表示期間が入ったときにガントチャートを表示するのでここは空文字にする
      end: ''
    });

  // ローディング用
  const [isLoading, setIsLoading] = useState(true);

  const handleEditSiteProcess = useCallback(() => {}, []);

  // 期間の検索用データ
  const [searchStartDate, setSearchStartDate] = useState(
    convertToFullCalendarDateString(today)
  );
  const [searchEndDate, setSearchEndDate] = useState(
    convertToFullCalendarEndDateString(today)
  );

  // 検索期間のバリデーション
  const formValidatePeriod = () => {
    if (!validateGanttPeriod(getValues('start_date'), getValues('end_date'))) {
      return validationMessage.ganttSearchPeriod;
    }

    return true;
  };

  // 検索の開始日が変更になったときのイベントハンドラ
  const handleChangeStartDatePicker = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setSearchStartDate(event.target.value);

    // 必須以外のバリデーションは開始日と終了日で共通なので、両方に同じエラーが発生しないように一度片方をリセット
    if (getValues('end_date') !== '') {
      clearErrors('end_date');
    }
  };

  const handleChangeEndDatePicker = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchEndDate(event.target.value);

    // 必須以外のバリデーションは開始日と終了日で共通なので、両方に同じエラーが発生しないように一度片方をリセット
    if (getValues('start_date') !== '') {
      clearErrors('start_date');
    }
  };

  // タスク一覧モーダル
  const [isOpenTaskListModal, setIsOpenTaskListModal] = useState(false);
  const [includeChildProcess, setIncludeChildProcess] = useState(false);
  const handleClickTaskCount = useCallback(
    (constructId: number, processId: number) => {
      setIsOpenTaskListModal(true);
      setSelectedConstructId(constructId);
      setSelectedProcessId(processId);
      setIncludeChildProcess(true);
    },
    []
  );

  // 初期表示エリアの設定
  const createInitialVisibleRange = () => {
    const date = new Date();
    const start = date.toISOString().split('T')[0].replaceAll('-', '/');
    date.setMonth(date.getMonth() + 1);
    const end = date.toISOString().split('T')[0].replaceAll('-', '/');

    return {
      start: convertToFullCalendarDateString(start),
      end: convertToFullCalendarDateString(end)
    };
  };

  // カレンダー一覧のデータに含まれる親工程の情報を工程一覧のデータに変換する。
  // ガントチャートに表示するためだけのデータのためid,名前,開始日,終了日だけ設定する。
  const convertParentSiteProcess = (
    calendarParentSiteProcess: TCalendarParentSiteProcess
  ) => {
    const siteProcess: TSiteProcessListData = {
      id: calendarParentSiteProcess.id,
      parent_site_process_id: null,
      parent_site_process_name: null,
      site_process_name: calendarParentSiteProcess.site_process_name,
      charge_user_name: null,
      start_date: calendarParentSiteProcess.start_date,
      end_date: calendarParentSiteProcess.end_date,
      site_task_count: 0,
      status: calendarParentSiteProcess.status,
      alert_count: 0,
      planned_man_day: null,
      actual_man_day: null,
      is_alerted: calendarParentSiteProcess.is_alerted,
      order_id: calendarParentSiteProcess.order_id
    };

    return siteProcess;
  };

  // カレンダー一覧のデータを工程一覧のデータに変換する
  const convertSiteProcess = (calendar: TCalendarListData) => {
    const siteProcess: TSiteProcessListData = {
      id: calendar.id,
      parent_site_process_id: calendar.parent_site_process
        ? calendar.parent_site_process.id
        : null,
      parent_site_process_name: calendar.parent_site_process
        ? calendar.parent_site_process.site_process_name
        : null,
      site_process_name: calendar.site_process_name,
      charge_user_name: calendar.charge_user_name,
      start_date: calendar.start_date,
      end_date: calendar.end_date,
      site_task_count: calendar.site_task_count,
      status: calendar.status,
      alert_count: calendar.alert_count,
      planned_man_day: calendar.planned_man_day,
      actual_man_day: calendar.actual_man_day,
      is_alerted: calendar.is_alerted,
      order_id: calendar.order_id
    };

    return siteProcess;
  };

  // カレンダー一覧を取得してガントチャート用のデータを作成する
  const createGanttResourceAndEvent = async () => {
    const ganttResourceList: Array<TGanttResource> = [];
    const ganttEventList: Array<TGanttEvent> = [];

    if (userId === 0) {
      return { resources: ganttResourceList, events: ganttEventList };
    }

    // 担当者が選択された時
    const resData = await getCalendarList(userId, undefined, undefined);
    const data = await checkResData(resData, enqueueSnackbar);

    if (data) {
      const calendarDataList = data.data as Array<TCalendarListData>;

      // 担当している大工程のidのリストを作成
      const parentProcessIdList = calendarDataList
        .filter(calendar => !calendar.parent_site_process)
        .map(calendar => calendar.id);

      // カレンダー一覧を工程一覧に変換してガント用データに変換する
      calendarDataList.forEach((calendar, index) => {
        // 工程に変換
        const siteProcess = convertSiteProcess(calendar);

        // イベントを作成
        ganttEventList.push(
          createGanttEvent(
            siteProcess,
            true,
            !siteProcess.parent_site_process_id
          )
        );

        // 大工程の場合はリソースを作成する。小工程で大工程がカレンダー一覧にない場合は大工程を作成する
        let parentSiteProcess: TSiteProcessListData | null = null;
        if (!calendar.parent_site_process) {
          parentSiteProcess = siteProcess;
        } else if (
          !parentProcessIdList.includes(calendar.parent_site_process.id)
        ) {
          parentProcessIdList.push(calendar.parent_site_process.id);
          parentSiteProcess = convertParentSiteProcess(
            calendar.parent_site_process
          );
          ganttEventList.push(createGanttEvent(parentSiteProcess, true, true));
        }
        if (parentSiteProcess) {
          // 大工程＋小工程のタスクのアラート件数を数えてガント用のリソースを作成
          let allAlertSiteTaskCount = parentSiteProcess.alert_count;
          calendarDataList.forEach(calendar => {
            if (calendar.id !== parentSiteProcess?.id) {
              if (
                calendar.parent_site_process &&
                calendar.parent_site_process.id === parentSiteProcess?.id
              ) {
                allAlertSiteTaskCount += calendar.alert_count;
              }
            }
          });
          ganttResourceList.push(
            createGanttResource(
              parentSiteProcess,
              parentSiteProcess.site_task_count,
              allAlertSiteTaskCount,
              false,
              calendar.constructs
            )
          );
        }
      });
    }

    return { resources: ganttResourceList, events: ganttEventList };
  };

  // リソースエリアに表示する項目毎の設定
  const resourceAreaConstructName: ColSpec = {
    field: 'constructName',
    headerContent: '案件',
    headerClassNames: 'resource-default resource-width-large',
    cellClassNames: 'resource-default resource-width-large',
    cellContent: args => args.resource?.extendedProps.construct?.name
  };
  const resourceAreaProcessName: ColSpec = {
    field: 'title',
    headerContent: '工程名',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: args => args.resource?.title
  };

  const resourceAreaTaskCount: ColSpec = {
    field: 'alertTaskCount',
    headerContent: 'アラート',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: args => {
      if (args.resource?.extendedProps.allAlertSiteTaskCount === 0) {
        return '';
      }

      return (
        <>
          <CustomTooltip title={tooltipContents.CalenderAlert}>
            <button
              className='resource-content-button'
              onClick={() => {
                if (args.resource) {
                  handleClickTaskCount(
                    args.resource.extendedProps.construct
                      ? args.resource.extendedProps.construct.id
                      : 0,
                    args.resource.extendedProps.siteProcess.id
                  );
                }
              }}
            >
              <Typography>
                {args.resource?.extendedProps.allAlertSiteTaskCount > 0 && (
                  <ReportProblemIcon />
                )}
                {`${args.resource?.extendedProps.allAlertSiteTaskCount}件`}
              </Typography>
            </button>
          </CustomTooltip>
        </>
      );
    }
  };

  // リソースに表示する内容の設定
  const [initialResourceAreaColumns] = useState([
    resourceAreaConstructName,
    resourceAreaProcessName,
    resourceAreaTaskCount
  ]);

  // userID切り替わり時のカレンダー一覧取得
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { resources, events } = await createGanttResourceAndEvent();
      const visibleRange = createInitialVisibleRange();
      setSearchStartDate(visibleRange.start);
      setSearchEndDate(visibleRange.end);
      reset({
        start_date: visibleRange.start,
        end_date: visibleRange.end
      });
      setInitialGanttData({
        resources,
        events
      });
      setIsLoading(false);
    })();
  }, [userId]);

  // 検索日時変更の表示範囲の設定
  useEffect(() => {
    if (
      searchStartDate &&
      searchEndDate &&
      searchStartDate <= searchEndDate &&
      validateGanttPeriod(searchStartDate, searchEndDate || '')
    ) {
      const newVisibleRange = {
        start: searchStartDate,
        end: convertToFullCalendarEndDateString(searchEndDate)
      };
      if (calendarRef.current) {
        calendarRef.current.getApi().setOption('visibleRange', newVisibleRange);
      } else {
        setInitialVisibleRange(newVisibleRange);
      }
    }
  }, [searchStartDate, searchEndDate]);

  // ガントの高さとリソースエリアの幅の設定
  const [initialHeight, setInitialHeight] = useState(GANTT_MINIMUM_HEIGHT);
  const [initialResourceAreaWidth, setInitialResourceAreaWidth] = useState(
    `${GANTT_RESOURCE_MIN_WIDTH_RATIO}%`
  );

  // ウインドウサイズに合わせたガントのサイズ調整
  useEffect(() => {
    // ウインドウサイズ変更時の処理とイベントハンドラ。イベントハンドラが複数登録されないようにuseEffect内で定義。
    const calculateHeight = () => {
      // 画面内に検索エリア＋ガントエリア＋フッターが収まるようにガントの高さを計算する
      const windowHeight = document.documentElement.clientHeight;
      const headerElement = document.getElementsByTagName('header');
      const headerHeight =
        headerElement && headerElement[0]
          ? headerElement[0].getBoundingClientRect().height
          : 0;
      const footerElement = document.getElementsByClassName('footer');
      const footerHeight =
        footerElement && footerElement[0]
          ? footerElement[0].getBoundingClientRect().height
          : 0;
      const searchContainerElement =
        document.getElementById(GANTT_SEARCH_HTML_ID);
      const searchContainerHeight = searchContainerElement
        ? searchContainerElement.getBoundingClientRect().height
        : 0;
      let height =
        windowHeight - footerHeight - headerHeight - searchContainerHeight - 60; // 60はマージンなどの余白分
      if (height < GANTT_MINIMUM_HEIGHT) {
        height = GANTT_MINIMUM_HEIGHT;
      }

      return height;
    };
    const calculateResourceAreaWidth = () => {
      // 工程とタスク件数の幅分のデータをとる
      const element = document.getElementById(GANTT_CHART_HTML_ID);
      let width = 0;
      if (element) {
        const clientRect = element.getBoundingClientRect();
        width = clientRect.width;
      }

      // リソースエリア全体がウインドウの半分に入りきらない場合はリソースエリアのサイズを変更する
      if (GANTT_CALENDAR_RESOURCE_WIDTH > width / 2) {
        return `${GANTT_RESOURCE_MIN_WIDTH_RATIO * 100}%`;
      }

      return `${GANTT_CALENDAR_RESOURCE_WIDTH}px`;
    };

    const handleResizeWindow = () => {
      const height = calculateHeight();
      const resourceAreaWidth = calculateResourceAreaWidth();
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.setOption('height', height);
        calendarApi.setOption('resourceAreaWidth', resourceAreaWidth);
      }
    };
    window.addEventListener('resize', handleResizeWindow);

    if (calendarRef.current) {
      // ウインドウサイズ変更時
      const calendarApi = calendarRef.current.getApi();
      calendarApi.setOption('resourceAreaWidth', calculateResourceAreaWidth());
    } else {
      // 初期設定
      setInitialHeight(calculateHeight());
      setInitialResourceAreaWidth(calculateResourceAreaWidth);
    }

    // handleResizeWindowが複数イベントに登録されないように一度削除
    return () => {
      window.removeEventListener('resize', handleResizeWindow);
    };
  }, []);

  // カレンダー一覧を再読み込みしてタスク件数に変更がある箇所を更新する（全件更新は処理時間がかかるため部分更新）
  const afterUpdate = async () => {
    if (!calendarRef.current) {
      return;
    }
    const calendarApi = calendarRef.current.getApi();
    const { resources, events } = await createGanttResourceAndEvent();
    // リソースの差し替え
    resources.forEach(resource => {
      const calendarResource = calendarApi.getResourceById(resource.id);
      if (calendarResource) {
        if (
          calendarResource.extendedProps.allAlertSiteTaskCount !==
          resource.extendedProps.allAlertSiteTaskCount
        ) {
          calendarResource.setExtendedProp(
            'allAlertSiteTaskCount',
            resource.extendedProps.allAlertSiteTaskCount
          );
          calendarResource.setExtendedProp(
            'process',
            resource.extendedProps.siteProcess
          );
        }
      }
    });
    // イベントの差し替え
    events.forEach(event => {
      const calendarEvent = calendarApi.getEventById(event.id);
      if (calendarEvent) {
        if (
          calendarEvent.extendedProps.siteProcess.alert_count !==
          event.extendedProps.siteProcess.alert_count
        ) {
          calendarEvent.setExtendedProp(
            'process',
            event.extendedProps.siteProcess
          );
        }
      }
    });
  };

  // ガントチャート上での工程名並び替え
  const handleResourceOrderChange = useCallback(
    (resources: TGanttResource[]) => {},
    []
  );

  return {
    calendarRef,
    initialHeight,
    initialGanttData,
    searchStartDate,
    searchEndDate,
    handleChangeStartDatePicker,
    handleChangeEndDatePicker,
    isOpenTaskListModal,
    setIsOpenTaskListModal,
    isLoading,
    initialVisibleRange,
    initialResourceAreaColumns,
    initialResourceAreaWidth,
    selectedConstructId,
    selectedProcessId,
    afterUpdate,
    handleEditSiteProcess,
    handleResourceOrderChange,
    formValidatePeriod,
    includeChildProcess,
    setIncludeChildProcess
  };
};
